import React, { useState } from 'react';
import { NotificationsActive, NotificationsNone, NotificationsOff } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { toggleListNotification, useNotificationSubscriptions } from '../services/api';
import { useLogin } from '../contexts/LoginContext';
import journeyTracker from '../utils/journeyTracker';

const NotificationBell = ({ listId, isLoggedIn, userEmail, className = '', size = 'small', variant = 'icon', subscribeText = 'Subscribe', subscribedText = 'Subscribed' }) => {
  const queryClient = useQueryClient();
  const { data: subscribedLists = [], isLoading } = useNotificationSubscriptions(userEmail);
  const [isHovered, setIsHovered] = useState(false);
  const { triggerLogin } = useLogin();

  const isSubscribed = subscribedLists.includes(listId);
  const [isToggling, setIsToggling] = React.useState(false);

  // Size variants for different contexts
  const sizeConfig = {
    small: { fontSize: 'small', padding: 'p-1.5' },
    medium: { fontSize: 'medium', padding: 'p-1' },
    large: { fontSize: 'large', padding: 'p-0.5' }
  };
  const { fontSize, padding } = sizeConfig[size] || sizeConfig.small;

  // Pill variant styles - more prominent
  const getPillStyles = (isActive, isLoggedOut) => {
    if (isLoggedOut) {
      // Interactive but shows login prompt on hover
      return 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 shadow-sm';
    }
    if (isActive) {
      return 'bg-blue-500 text-white hover:bg-blue-600 shadow-sm';
    }
    return 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-700 shadow-sm';
  };

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isLoggedIn || isToggling) return;

    setIsToggling(true);
    try {
      await toggleListNotification(listId, !isSubscribed);
      // Track subscribe/unsubscribe action
      journeyTracker.trackAction(isSubscribed ? 'notification_unsubscribe' : 'notification_subscribe', { list_id: listId });
      queryClient.invalidateQueries(['notificationSubscriptions', userEmail]);
    } catch (error) {
      console.error('[NotificationBell] Failed to toggle notification:', error);
    } finally {
      setIsToggling(false);
    }
  };

  // Pill variant rendering
  if (variant === 'pill') {
    const pillBase = 'inline-flex items-center justify-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap';

    if (!isLoggedIn) {
      const handleLoginClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        triggerLogin();
      };

      return (
        <button
          type="button"
          className={`${pillBase} ${getPillStyles(false, true)} ${className}`}
          title="Log in to subscribe"
          onClick={handleLoginClick}
        >
          <NotificationsNone style={{ fontSize: 14 }} />
          <span>{subscribeText}</span>
        </button>
      );
    }

    if (isLoading || isToggling) {
      return (
        <button
          type="button"
          className={`${pillBase} ${getPillStyles(false, true)} animate-pulse ${className}`}
          disabled
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          <NotificationsNone style={{ fontSize: 14 }} />
          <span>{subscribeText}</span>
        </button>
      );
    }

    return (
      <button
        type="button"
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`${pillBase} ${getPillStyles(isSubscribed, false)} ${className}`}
        title={isSubscribed ? "Click to unsubscribe" : "Click to subscribe"}
      >
        {isSubscribed ? (
          <>
            <NotificationsActive style={{ fontSize: 14 }} />
            <span>{isHovered ? 'Unsubscribe' : subscribedText}</span>
          </>
        ) : (
          <>
            <NotificationsNone style={{ fontSize: 14 }} />
            <span>{subscribeText}</span>
          </>
        )}
      </button>
    );
  }

  // Icon variant (default)
  if (!isLoggedIn) {
    return (
      <button
        type="button"
        className={`${padding} text-gray-300 cursor-not-allowed ${className}`}
        disabled
        title="Log in to enable notifications"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      >
        <NotificationsOff fontSize={fontSize} />
      </button>
    );
  }

  if (isLoading || isToggling) {
    return (
      <button
        type="button"
        className={`${padding} text-gray-400 animate-pulse ${className}`}
        disabled
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      >
        <NotificationsNone fontSize={fontSize} />
      </button>
    );
  }

  if (isSubscribed) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={`${padding} text-blue-500 hover:text-blue-600 cursor-pointer ${className}`}
        title="Click to unsubscribe from notifications"
      >
        <NotificationsActive fontSize={fontSize} />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`${padding} text-gray-400 hover:text-blue-500 cursor-pointer ${className}`}
      title="Click to subscribe to notifications"
    >
      <NotificationsNone fontSize={fontSize} />
    </button>
  );
};

export default NotificationBell;
